import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Button, Modal, useGrpcMetadata } from '@parca/components';
import ResultBox from './ResultBox';
const ProfileShareModal = ({ isOpen, closeModal, queryRequest, queryClient, }) => {
    const [isShared, setIsShared] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [description, setDescription] = useState('');
    const [sharedLink, setSharedLink] = useState('');
    const metadata = useGrpcMetadata();
    const isFormDataValid = () => true;
    const handleSubmit = async () => {
        try {
            setLoading(true);
            const { response } = await queryClient.shareProfile({ queryRequest, description }, { meta: metadata });
            setSharedLink(response.link);
            setLoading(false);
            setIsShared(true);
        }
        catch (err) {
            if (err instanceof Error) {
                console.error(err);
                setLoading(false);
                // https://github.com/microsoft/TypeScript/issues/38347
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                setError(err.toString());
            }
        }
    };
    const onClose = () => {
        setLoading(false);
        setError('');
        setDescription('');
        setIsShared(false);
        closeModal();
    };
    return (_jsx(Modal, { isOpen: isOpen, closeModal: onClose, title: "Share Profile", className: "w-[420px]", children: _jsxs("div", { className: "py-2", children: [_jsx("p", { className: "text-sm text-gray-500 dark:text-gray-300", children: "Note: Shared profiles can be accessed by anyone with the link, even from people outside your organisation." }), !isShared || error?.length > 0 ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "mb-2 mt-3 text-sm text-gray-500 dark:text-gray-300", children: "Enter a description (optional)" }), _jsx("textarea", { className: "w-full border bg-inherit px-2 py-2 text-sm text-gray-500 dark:text-gray-300", value: description, onChange: e => setDescription(e.target.value) }), _jsx(Button, { variant: "primary", className: "mt-4", onClick: e => {
                                e.preventDefault();
                                void handleSubmit();
                            }, disabled: loading || !isFormDataValid(), type: "submit", children: loading ? 'Sharing' : 'Share' }), error !== '' ? _jsx("p", { children: "Something went wrong please try again" }) : null] })) : (_jsxs(_Fragment, { children: [_jsx(ResultBox, { value: sharedLink, className: "mt-4" }), _jsx("div", { className: "mt-8 flex justify-center", children: _jsx(Button, { variant: "neutral", className: "w-fit", onClick: onClose, children: "Close" }) })] }))] }) }));
};
const ProfileShareButton = ({ queryRequest, queryClient, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: "neutral", onClick: () => setIsOpen(true), disabled: disabled, className: "gap-2", id: "h-share-profile-button", children: ["Share profile", _jsx(Icon, { icon: "material-symbols:share", width: 20 })] }), _jsx(ProfileShareModal, { isOpen: isOpen, closeModal: () => setIsOpen(false), queryRequest: queryRequest, queryClient: queryClient })] }));
};
export default ProfileShareButton;
